// Client-side media preparation: compress photos, make tiny inline
// thumbnails (shown blurred while the full file loads), read video frames,
// record voice notes and compute their waveform.
import type { Kind, Media } from "./db";
import { isPdf } from "./docs";

const MAX_IMAGE_EDGE = 1600;
const HD_IMAGE_EDGE = 4096;
const THUMB_EDGE = 48;

export interface Prepared {
  kind: Kind;
  media: Media; // includes the blob to upload
}

export function kindOf(mime: string, voice = false): Kind {
  if (voice) return "voice";
  if (mime.startsWith("image/") && mime !== "image/svg+xml") return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
}

/** `hd`: photos keep up to 4096px at high quality (like WhatsApp's HD). */
export async function prepareFile(file: File, opts: { hd?: boolean } = {}): Promise<Prepared> {
  const kind = kindOf(file.type);
  const base: Media = { mime: file.type || "application/octet-stream", size: file.size, name: file.name, blob: file };
  try {
    if (kind === "image") return { kind, media: await prepareImage(file, base, !!opts.hd) };
    if (kind === "video") return { kind, media: await prepareVideo(file, base) };
    if (kind === "audio") return { kind, media: { ...base, duration: await audioDuration(file) } };
    // Documents: a PDF's first page and page count, like WhatsApp.
    if (isPdf(base.mime, file.name)) return { kind, media: { ...base, mime: "application/pdf", ...(await (await import("./docs")).pdfPreview(file)) } };
  } catch (e) {
    console.warn("media prep failed, sending as file", e);
    return { kind: "file", media: base };
  }
  return { kind, media: base };
}

async function prepareImage(file: File, base: Media, hd: boolean): Promise<Media> {
  const bmp = await createImageBitmap(file);
  const { width, height } = bmp;
  const thumb = await drawThumb(bmp, width, height);
  let blob: Blob = file;
  let w = width;
  let h = height;
  // Like WhatsApp: re-encode big photos (HD: much less). Keep GIFs
  // (animation) and small files.
  const edge = hd ? HD_IMAGE_EDGE : MAX_IMAGE_EDGE;
  const big = Math.max(width, height) > edge || file.size > (hd ? 8_000_000 : 1_500_000);
  if (big && file.type !== "image/gif") {
    const scale = Math.min(1, edge / Math.max(width, height));
    w = Math.round(width * scale);
    h = Math.round(height * scale);
    const c = new OffscreenCanvas(w, h);
    c.getContext("2d")!.drawImage(bmp, 0, 0, w, h);
    blob = await c.convertToBlob({ type: "image/jpeg", quality: hd ? 0.92 : 0.82 });
  }
  bmp.close();
  return {
    ...base,
    blob,
    mime: blob === file ? base.mime : "image/jpeg",
    size: blob.size,
    name: blob === file ? file.name : file.name.replace(/\.\w+$/, "") + ".jpg",
    width: w,
    height: h,
    thumb,
  };
}

async function drawThumb(src: CanvasImageSource, width: number, height: number): Promise<string> {
  const s = THUMB_EDGE / Math.max(width, height);
  const w = Math.max(1, Math.round(width * s));
  const h = Math.max(1, Math.round(height * s));
  const c = new OffscreenCanvas(w, h);
  c.getContext("2d")!.drawImage(src, 0, 0, w, h);
  const b = await c.convertToBlob({ type: "image/jpeg", quality: 0.5 });
  return blobToDataURL(b);
}

function prepareVideo(file: File, base: Media): Promise<Media> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.muted = true;
    v.preload = "metadata";
    v.src = url;
    const done = (m: Media | Error) => {
      URL.revokeObjectURL(url);
      m instanceof Error ? reject(m) : resolve(m);
    };
    v.onerror = () => done(new Error("cannot read video"));
    v.onloadedmetadata = () => {
      v.currentTime = Math.min(0.5, (v.duration || 1) / 2);
    };
    v.onseeked = async () => {
      try {
        const thumb = await drawThumb(v, v.videoWidth, v.videoHeight);
        done({ ...base, width: v.videoWidth, height: v.videoHeight, duration: v.duration, thumb });
      } catch (e) {
        done(e as Error);
      }
    };
  });
}

async function audioDuration(blob: Blob): Promise<number | undefined> {
  const url = URL.createObjectURL(blob);
  try {
    const a = new Audio();
    a.preload = "metadata";
    a.src = url;
    await new Promise((r, j) => ((a.onloadedmetadata = r), (a.onerror = j)));
    return Number.isFinite(a.duration) ? a.duration : undefined;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function blobToDataURL(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

// ---- voice notes -------------------------------------------------------------

export interface Recording {
  stop(): Promise<Prepared | null>;
  cancel(): void;
  /** live input level 0..1 for the recording UI */
  level(): number;
  startedAt: number;
}

export async function recordVoice(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  const mime = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32_000 } : undefined);
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const buf = new Uint8Array(analyser.fftSize);
  rec.start(250);
  const startedAt = Date.now();

  const release = () => {
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close();
  };
  return {
    startedAt,
    level() {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      return Math.min(1, peak / 90);
    },
    cancel() {
      rec.onstop = null;
      if (rec.state !== "inactive") rec.stop();
      release();
    },
    stop() {
      return new Promise((resolve) => {
        rec.onstop = async () => {
          release();
          const duration = (Date.now() - startedAt) / 1000;
          if (duration < 0.7) return resolve(null); // accidental tap
          const type = (rec.mimeType || mime || "audio/webm").split(";")[0];
          const blob = new Blob(chunks, { type });
          const waveform = await computeWaveform(blob).catch(() => undefined);
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          resolve({
            kind: "voice",
            media: { blob, mime: type, size: blob.size, name: `voice-${new Date().toISOString().slice(0, 19)}.${ext}`, duration, waveform },
          });
        };
        rec.stop();
      });
    },
  };
}

/** 48 bars, 0..100, of the loudness over time. */
export async function computeWaveform(blob: Blob, bars = 48): Promise<number[]> {
  const ctx = new OfflineAudioContext(1, 1, 44100);
  const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
  const data = audio.getChannelData(0);
  const step = Math.max(1, Math.floor(data.length / bars));
  const out: number[] = [];
  for (let i = 0; i < bars; i++) {
    let sum = 0;
    const end = Math.min(data.length, (i + 1) * step);
    for (let j = i * step; j < end; j++) sum += data[j] * data[j];
    out.push(Math.sqrt(sum / Math.max(1, end - i * step)));
  }
  const max = Math.max(...out, 1e-6);
  return out.map((v) => Math.round((v / max) * 100));
}

// ---- formatting ----------------------------------------------------------------

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} kB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

export function formatDuration(s = 0): string {
  s = Math.max(0, Math.round(s));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function fileExt(name = ""): string {
  const m = /\.([a-z0-9]{1,5})$/i.exec(name);
  return m ? m[1].toUpperCase() : "FILE";
}
